import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Popup } from 'semantic-ui-react';
import { chatWithAgent, getConversationBySessionId } from './helpers-goPilot';
import AgentPopupHeader from './components/AgentPopupHeader';
import AgentPopupInput from './components/AgentPopupInput';
import './GoFormzAgentPopup.scss';
import ChatMessages from './components/ChatMessages';
import { ConversationMessage, ConversationSessionDocument, MessageVote } from './goFormz-agent-types';
import { useGoFormzAgent } from './GoFormzAgentHook';
import AgentPopupSuggestions from './components/AgentPopupSuggestions';

interface GoFormzAgentPopupProps {
  formulaSelected?: (formula: string) => void;
}

export default function GoFormzAgentPopup({ formulaSelected }: GoFormzAgentPopupProps) {
  const { isAgentOpen, closeAgent, templateContext, setTemplateContext, selectFormula, selectedFormula } = useGoFormzAgent();

  const [aiSupportQuery, setAiSupportQuery] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  // Add this effect to load session ID from localStorage on component mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem('AI_SESSION_ID');
    if (storedSessionId) {
      setSessionId(storedSessionId);

      //if there is a session ID recall the conversation session
      getConversationBySessionId(storedSessionId)
        .then((response) => {
          if (response && response.messages) {
            setMessages(response.messages);
          }
        })
        .catch((error) => {
          console.error('Error fetching conversation by session ID:', error);
        });
    }
  }, []);

  // Handle state changes from the hook
  useEffect(() => {
    if (isAgentOpen) {
      // Ensure the popup is visible when isAgentOpen becomes true
      setIsVisible(true);
      if (bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }
    } else {
      // Clear the session when the popup is closed
      clearAgent();

      // Add a small delay before hiding to allow animations to complete
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAgentOpen]);

  // Handle formula selection from other components
  useEffect(() => {
    if (selectedFormula && formulaSelected) {
      formulaSelected(selectedFormula);
    }
  }, [selectedFormula, formulaSelected]);

  // Scroll to bottom whenever messages are added
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(
    async (queryOverride?: string) => {
      const query = typeof queryOverride === 'string' ? queryOverride.trim() : aiSupportQuery.trim();
      if (!query) return;

      setAiSupportQuery('');
      const userMessage: ConversationMessage = {
        agentMessage: false,
        message: query,
        vote: MessageVote.None,
        messageId: ''
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsSending(true);

      try {
        // Safely access templateContext with fallbacks to prevent errors
        const selectedFieldIds = templateContext?.selectedFieldIds || [];
        const selectedFieldId = selectedFieldIds.length > 0 ? selectedFieldIds[0] : null;

        // Use all the available context fields from templateContext
        const agentResponse = await chatWithAgent(
          templateContext?.templateId || '',
          templateContext?.currentPageId || '',
          templateContext?.selectedDataLayoutId || '',
          selectedFieldId,
          templateContext?.selectedProperty || '',
          templateContext?.selectedCalculation || '',
          query,
          sessionId // Use the current sessionId from state
        );

        // Update session ID from response and save to localStorage
        if (agentResponse.conversationSessionId != sessionId) {
          setSessionId(agentResponse.conversationSessionId);
          localStorage.setItem('AI_SESSION_ID', agentResponse.conversationSessionId);
        }

        setMessages((prev) => [...prev, agentResponse.agentMessage]);

        // Only update the template context if there are actual changes
        const updatedTemplateContext = { ...templateContext };
        let hasChanges = false;

        if (agentResponse.agentMessage.message !== templateContext?.selectedCalculation) {
          hasChanges = true;
        }

        if (hasChanges) {
          await setTemplateContext(updatedTemplateContext);
        }
      } catch (error) {
        console.error('Error communicating with agent:', error);
        const errorMessage: ConversationMessage = {
          agentMessage: true,
          message: 'Something went wrong. Please try again.',
          messageId: '',
          vote: MessageVote.None
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsSending(false);
      }
    },
    [aiSupportQuery, sessionId, templateContext, selectedFormula, selectFormula, setTemplateContext]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  // Handle local formula selection and propagate through the hook
  const handleFormulaSelected = useCallback(
    (formula: string) => {
      if (readOnly) return;
      selectFormula(formula);
      if (formulaSelected) {
        formulaSelected(formula);
      }
    },
    [selectFormula, formulaSelected, readOnly]
  );

  const clearAgent = () => {
    setReadOnly(false);
    setMessages([]);
    setAiSupportQuery('');
    setSessionId(null);
    localStorage.removeItem('AI_SESSION_ID');
    selectFormula(null);
  };

  const historyToggled = async (showing: boolean) => {
    setHistoryOpen(showing);
  };

  const historicalConversationSelected = async (conversation: ConversationSessionDocument) => {
    setHistoryOpen(false);
    setMessages(conversation.messages);
    setSessionId(conversation.sessionId);
    setReadOnly(true);
    localStorage.setItem('AI_SESSION_ID', conversation.sessionId);
  };

  const sendPromptForUser = async (key: string) => {
    await sendMessage(key);
  };

  return (
    <Popup basic open={isVisible} onClose={closeAgent} position="bottom right" popper={{ className: 'ai-calculation-builder-popup' }}>
      <div className="ai-popup-content">
        <AgentPopupHeader
          onClose={closeAgent}
          onClear={clearAgent}
          historyToggled={historyToggled}
          historicalConversationSelected={historicalConversationSelected}
        />
        {!historyOpen && (
          <>
            <AgentPopupSuggestions sendPromptForUser={sendPromptForUser} />
            <div className="ai-popup-body" ref={bodyRef}>
              <ChatMessages sessionId={sessionId} messages={messages} formulaSelected={handleFormulaSelected} />
            </div>
            {!readOnly && (
              <AgentPopupInput value={aiSupportQuery} onChange={setAiSupportQuery} onKeyDown={handleKeyDown} onSend={sendMessage} disabled={isSending} />
            )}
          </>
        )}
      </div>
    </Popup>
  );
}
